import type { VideoElement } from "@ai-teach/types";
import { Button, IconButton } from "@ai-teach/ui-web";
import { useRef, useState } from "react";
import { FaPause, FaPlay } from "react-icons/fa";

interface VideoRendererProps {
  element: VideoElement;
  isLiveMode?: boolean;
}

export function VideoRenderer({ element }: VideoRendererProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const togglePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  return (
    <div style={{ position: "relative", width: "100%", height: "100%" }}>
      <video
        ref={videoRef}
        src={element.media.src ?? ""}
        autoPlay={element.autoPlay}
        loop={element.loop}
        draggable={false}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        style={{
          width: "100%",
          height: "100%",
          objectFit: element.objectFit || "contain",
          pointerEvents: "none",
          userSelect: "none",
          borderRadius: element.style?.borderRadius ? `${element.style.borderRadius}px` : undefined,
          opacity: element.style?.opacity,
        }}
      >
        <track kind="captions" />
      </video>

      <IconButton
        onClick={togglePlayPause}
        variant="ghost"
        aria-label={isPlaying ? "Pause" : "Play"}
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "48px",
          height: "48px",
          borderRadius: "50%",
          backgroundColor: "rgba(0, 0, 0, 0.6)",
          pointerEvents: "auto",
          transition: "background-color 0.2s",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = "rgba(0, 0, 0, 0.8)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = "rgba(0, 0, 0, 0.6)";
        }}
      >
        {isPlaying ? <FaPause size={24} color="white" /> : <FaPlay size={24} color="white" />}
      </IconButton>
    </div>
  );
}
